import React, { FunctionComponent, useEffect, useState } from 'react'
import { Button, Loader, useVerifyingCountdown, Text } from '@afterpaytouch/core'
import { useConsumerSizes } from '../../../utils/responsive'
import { useTranslation } from 'next-i18next'
import {
  useAppRouter,
  useConsumerCountry,
  useConsumerName,
  useConsumerEmail,
  showToast,
  useAppDispatch,
  useBiometricApplicantId,
  useSkipBiometricCheck,
} from '../../../state'
import { Route } from '../../../config/router'
import {
  BIOMETRIC_VERIFYING_TIME_OUT,
  BiometricHardIdCheckHookStatus,
  isCountryAllowedToSkipDuringBiometricVerification,
  getIdentityVerificationFailedRoute,
} from '../../../model/identity'
import { LaunchDarklyFlags, useFlag } from '../../../hooks/useFlag'
import { ProfileCard } from '../../ProfileCard'
import { useBiometricHardIdCheck, useIdFlowErrorHandler } from '../hooks'
import style from './style.module.css'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { TrackingEvent } from '../../../model/amplitude'
import { VerifyLaterModal } from '../VerifyLaterModal/VerifyLaterModal'
import { FormWrapper } from '../FormWrapper'

export const VerifyingBiometric: FunctionComponent = () => {
  const router = useAppRouter()
  const country = useConsumerCountry()
  const consumerName = useConsumerName()
  const consumerEmail = useConsumerEmail() ?? ''
  const biometricApplicantId = useBiometricApplicantId()
  const manualIdvEnabled = useFlag(LaunchDarklyFlags.ManualIdvEnabled)
  const skipBiometricCheck = useSkipBiometricCheck()
  const { t } = useTranslation()
  const { handleError } = useIdFlowErrorHandler()
  const dispatch = useAppDispatch()
  const { status } = useBiometricHardIdCheck(biometricApplicantId, skipBiometricCheck)
  const amplitude = useAmplitudeWithEnduringEventProperties()
  const [showVerifyLaterModal, setShowVerifyLaterModal] = useState(false)
  const { buttonSize, textSize } = useConsumerSizes()

  const testNameSpace = 'biometric-verifying'
  const tNameSpace = 'verifyIdentity:verifying'

  const { isCountingDown: maxTimeoutCountingDown } = useVerifyingCountdown(BIOMETRIC_VERIFYING_TIME_OUT)

  const handleVerifyLaterClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_VERIFY_LATER_BUTTON)
    setShowVerifyLaterModal(true)
  }

  const handleModalClose = (): void => {
    setShowVerifyLaterModal(false)
  }

  const handleVerifyNowClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_VERIFY_NOW_BUTTON)
    setShowVerifyLaterModal(false)
  }

  const handleSkipClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_SKIP_BUTTON)
    setShowVerifyLaterModal(false)
    router.push(Route.HOME)
  }

  useEffect(() => {
    if (!maxTimeoutCountingDown) {
      handleError()
    }
  }, [maxTimeoutCountingDown])

  useEffect(() => {
    if (status === BiometricHardIdCheckHookStatus.INITIAL) {
      return
    }
    switch (status) {
      case BiometricHardIdCheckHookStatus.FAILED:
        router.push(getIdentityVerificationFailedRoute(manualIdvEnabled))
        break
      case BiometricHardIdCheckHookStatus.INVALID_APPLICANT_ID:
        handleError()
        break
      case BiometricHardIdCheckHookStatus.VERIFIED:
        dispatch(showToast({ toastType: 'accountVerified' }))
        router.push(Route.HOME)
        break
      default:
        router.push(Route.HOME)
        break
    }
  }, [status])

  return (
    <>
      <FormWrapper>
        <div className='mb-4'>
          <ProfileCard consumerName={consumerName} consumerEmail={consumerEmail} />
        </div>
        <Loader testNameSpace={testNameSpace} text={t(`${tNameSpace}:loader`)} textProps={{ bold: true }} />
        <div className='text-center'>
          <Text testNameSpace={testNameSpace} size={textSize}>
            {t(`${tNameSpace}:loaderExtended`)}
          </Text>
        </div>
      </FormWrapper>
      {isCountryAllowedToSkipDuringBiometricVerification(country) && (
        <>
          <div className={style.countdownTimerVerifyLaterButtonWrapper}>
            <Button.Primary testNameSpace='verify-identity-verify-later' onClick={handleVerifyLaterClick} padding='Fluid'>
              {t('verifyIdentity:verifyLater:continueWhileWeVerify')}
            </Button.Primary>
          </div>
          <VerifyLaterModal
            show={showVerifyLaterModal}
            onClose={handleModalClose}
            onVerifyNow={handleVerifyNowClick}
            onSkip={handleSkipClick}
            testNameSpace='verify-later'
            buttonSize={buttonSize}
          />
        </>
      )}
    </>
  )
}
